import React, { useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getTour } from "@/api";
import { useLang, localized } from "@/i18n";
import { waLink } from "@/config";
import { Clock, MapPin, Check, X, Info, Video, Camera, Sparkles } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export default function TourDetailPage() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { data: tour, isLoading } = useQuery({ queryKey: ["tour", slug], queryFn: () => getTour(slug) });
  const [activeImg, setActiveImg] = useState(0);

  const bookQuery = () => {
    const q = new URLSearchParams({ tour: tour.slug });
    if (params.get("date")) q.set("date", params.get("date"));
    if (params.get("pax")) q.set("pax", params.get("pax"));
    return `/reservar?${q.toString()}`;
  };

  if (isLoading) {
    return <div data-testid="tour-detail-loading" className="pt-40 pb-24 max-w-[1440px] mx-auto px-6 md:px-12 text-[#A3A095]">Cargando…</div>;
  }

  if (!tour) {
    return (
      <div data-testid="tour-not-found" className="pt-40 pb-32 max-w-2xl mx-auto px-6 text-center">
        <h1 className="font-serif-hc text-4xl text-[#F5F2EA]">
          {lang === "es" ? "Experiencia no encontrada" : "Experience not found"}
        </h1>
        <p className="mt-4 text-[#A3A095]">
          {lang === "es" ? "Este paquete ya no está disponible, pero tenemos otros que te van a encantar." : "This package is no longer available, but we have others you'll love."}
        </p>
        <Link to="/tours" data-testid="tour-not-found-cta" className="hc-btn-primary inline-block mt-8">
          {t("tours.all")}
        </Link>
      </div>
    );
  }

  const price = tour.price_mxn > 0 ? `MX$${tour.price_mxn.toLocaleString()}` : t("tours.consult");
  const waMsg = `Hola, me interesa reservar el tour "${localized(tour.name, lang)}". Me gustaría conocer disponibilidad.`;

  return (
    <div data-testid="tour-detail-page" className="pt-24 md:pt-28">
      {/* Gallery */}
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 pt-6">
        <div className="grid md:grid-cols-4 gap-3 md:h-[560px]">
          <div className="md:col-span-3 h-[380px] md:h-full overflow-hidden">
            <img src={tour.gallery?.[activeImg] || tour.hero_image} alt="" className="w-full h-full object-cover" />
          </div>
          <div className="grid grid-cols-3 md:grid-cols-1 gap-3 md:h-full">
            {(tour.gallery || []).slice(0, 3).map((g, i) => (
              <button
                key={i}
                onClick={() => setActiveImg(i)}
                data-testid={`gallery-thumb-${i}`}
                className={`overflow-hidden aspect-[4/3] md:h-1/3 border ${activeImg === i ? "border-[#C4A661]" : "border-transparent"}`}
              >
                <img src={g} alt="" className="w-full h-full object-cover hover:scale-105 transition" />
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-14 md:py-20 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-8">
          <p className="eyebrow mb-4 flex items-center gap-2"><MapPin size={12} /> {localized(tour.location, lang)}</p>
          <h1 className="font-serif-hc font-light text-4xl md:text-6xl leading-[1.02] text-[#F5F2EA]">
            {localized(tour.name, lang)}
          </h1>

          <div className="flex flex-wrap gap-6 mt-6 text-sm text-[#A3A095]">
            <span className="inline-flex items-center gap-2"><Clock size={14} /> {localized(tour.duration, lang)}</span>
            <span className="inline-flex items-center gap-2"><MapPin size={14} /> {localized(tour.location, lang)}</span>
          </div>

          <p className="mt-10 text-lg text-[#F5F2EA]/85 leading-relaxed max-w-2xl">
            {localized(tour.description, lang)}
          </p>

          {/* Includes / Excludes */}
          <div className="mt-14 grid md:grid-cols-2 gap-10">
            <div>
              <p className="eyebrow mb-5">{t("common.includes")}</p>
              <ul className="space-y-3">
                {(tour.includes?.[lang] || []).map((it, i) => (
                  <li key={i} className="flex gap-3 text-[15px] text-[#F5F2EA]"><Check size={16} className="text-[#139090] mt-1 shrink-0" />{it}</li>
                ))}
              </ul>
            </div>
            <div>
              <p className="eyebrow mb-5">{t("common.excludes")}</p>
              <ul className="space-y-3">
                {(tour.excludes?.[lang] || []).map((it, i) => (
                  <li key={i} className="flex gap-3 text-[15px] text-[#A3A095]"><X size={16} className="text-[#A3A095] mt-1 shrink-0" />{it}</li>
                ))}
              </ul>
            </div>
          </div>

          {/* Itinerary */}
          {(tour.itinerary?.[lang] || []).length > 0 && (
            <div className="mt-16">
              <p className="eyebrow mb-6">{t("common.itinerary")}</p>
              <div className="relative pl-6 border-l border-[#1E2B25] space-y-8">
                {tour.itinerary[lang].map((st, i) => (
                  <div key={i} className="relative">
                    <span className="absolute -left-[27px] top-1 w-2 h-2 bg-[#C4A661] rounded-full" />
                    <p className="font-mono text-xs text-[#C4A661] tracking-[0.2em]">{st.time}</p>
                    <h4 className="font-serif-hc text-xl text-[#F5F2EA] mt-1">{st.title}</h4>
                    {st.detail && <p className="text-[#A3A095] text-sm mt-1">{st.detail}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Extras promo (all balloon packages) */}
          {tour.price_mxn > 0 && (
            <div data-testid="tour-extras-promo" className="mt-16 border border-[#C4A661]/30 bg-[#0C1310] p-8 md:p-10">
              <p className="eyebrow mb-2 flex items-center gap-2 text-[#C4A661]">
                <Sparkles size={12} /> {t("extras_promo.eyebrow")}
              </p>
              <h3 className="font-serif-hc font-light text-3xl md:text-4xl text-[#F5F2EA]">{t("extras_promo.title")}</h3>
              <div className="mt-8 grid sm:grid-cols-2 gap-6">
                <div data-testid="tour-extra-drone" className="border border-[#1E2B25] p-6">
                  <Video size={22} className="text-[#139090]" strokeWidth={1.5} />
                  <h4 className="font-serif-hc text-xl text-[#F5F2EA] mt-4">{t("extras_promo.drone")}</h4>
                  <p className="text-sm text-[#A3A095] mt-2 leading-relaxed">{t("extras_promo.drone_d")}</p>
                  <p className="font-serif-hc text-2xl text-[#C4A661] mt-4">MX$1,500</p>
                </div>
                <div data-testid="tour-extra-photos" className="border border-[#1E2B25] p-6">
                  <Camera size={22} className="text-[#139090]" strokeWidth={1.5} />
                  <h4 className="font-serif-hc text-xl text-[#F5F2EA] mt-4">{t("extras_promo.photos")}</h4>
                  <p className="text-sm text-[#A3A095] mt-2 leading-relaxed">{t("extras_promo.photos_d")}</p>
                  <p className="font-serif-hc text-2xl text-[#C4A661] mt-4">MX$1,500</p>
                </div>
              </div>
              <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-t border-[#1E2B25] pt-6">
                <p data-testid="tour-extras-bundle" className="text-[#C4A661]">{t("extras_promo.bundle")}</p>
                <button
                  data-testid="tour-extras-cta"
                  onClick={() => nav(bookQuery())}
                  className="hc-btn-secondary whitespace-nowrap"
                >
                  {t("extras_promo.cta")}
                </button>
              </div>
            </div>
          )}

          {/* Meeting + Recs */}
          <div className="mt-14 grid md:grid-cols-2 gap-10">
            <div>
              <p className="eyebrow mb-4">{t("common.meeting")}</p>
              <p className="text-[#F5F2EA]">{localized(tour.meeting_point, lang)}</p>
            </div>
            <div>
              <p className="eyebrow mb-4">{t("common.recs")}</p>
              <ul className="space-y-2">
                {(tour.recommendations?.[lang] || []).map((r, i) => (
                  <li key={i} className="flex gap-3 text-[15px] text-[#F5F2EA]"><Info size={14} className="text-[#C4A661] mt-1.5 shrink-0" />{r}</li>
                ))}
              </ul>
            </div>
          </div>

          {/* FAQ / Reviews */}
          <div className="mt-16">
            <p className="eyebrow mb-4">{t("nav.faq")}</p>
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="a" className="border-b border-[#1E2B25]">
                <AccordionTrigger className="text-[#F5F2EA] hover:text-[#C4A661] text-left">{lang === "es" ? "¿Cómo se confirma la reserva?" : "How is booking confirmed?"}</AccordionTrigger>
                <AccordionContent className="text-[#A3A095]">{lang === "es" ? "Una vez recibida tu reserva, un asesor te contactará por WhatsApp o email para confirmar disponibilidad y detalles de pago." : "Once your booking is received, an advisor will contact you via WhatsApp or email to confirm availability and payment details."}</AccordionContent>
              </AccordionItem>
              <AccordionItem value="b" className="border-b border-[#1E2B25]">
                <AccordionTrigger className="text-[#F5F2EA] hover:text-[#C4A661] text-left">{lang === "es" ? "¿Puedo cancelar o reprogramar?" : "Can I cancel or reschedule?"}</AccordionTrigger>
                <AccordionContent className="text-[#A3A095]">{lang === "es" ? "Sí. Las políticas se detallan al confirmar tu reserva según el tipo de experiencia." : "Yes. Policies are shared upon booking confirmation depending on the experience."}</AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>

        {/* Sticky booking column */}
        <aside className="md:col-span-4">
          <div className="md:sticky md:top-28 bg-[#0C1310] border border-[#1E2B25] p-8">
            {tour.label && (
              <span className="inline-block px-3 py-1 bg-[#C4A661] text-[#050908] text-[10px] tracking-[0.22em] uppercase font-semibold mb-4">
                {localized(tour.label, lang)}
              </span>
            )}
            <p className="text-xs tracking-[0.22em] uppercase text-[#A3A095]">{t("tours.from")}</p>
            <p className="font-serif-hc text-4xl text-[#C4A661] mt-1">{price}</p>
            <p className="text-xs text-[#A3A095] mt-1">{t("common.per_person")}</p>

            <div className="mt-6 space-y-3 text-sm text-[#A3A095]">
              <div className="flex justify-between border-b border-[#1E2B25] pb-2"><span>{t("common.duration")}</span><span className="text-[#F5F2EA]">{localized(tour.duration, lang)}</span></div>
              <div className="flex justify-between border-b border-[#1E2B25] pb-2"><span>{t("common.location")}</span><span className="text-[#F5F2EA]">{localized(tour.location, lang)}</span></div>
            </div>

            <button
              onClick={() => nav(bookQuery())}
              data-testid="book-this-tour"
              className="hc-btn-primary w-full mt-8"
            >
              {t("common.book_this")}
            </button>

            <div className="mt-8 border-t border-[#1E2B25] pt-6" data-testid="tour-wa-expert">
              <p className="text-[#F5F2EA] font-medium">{t("common.expert_q")}</p>
              <p className="text-sm text-[#A3A095] mt-1">{t("common.expert_sub")}</p>
              <a
                href={waLink(waMsg)}
                target="_blank"
                rel="noreferrer"
                data-testid="book-wa-tour"
                className="hc-btn-wa w-full mt-4"
              >
                {t("common.expert_cta")}
              </a>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
